import { ArrowRight, ReceiptText } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { cabasPrice, cabasPriceDetails } from '@/utils/cabas'
import { EstimateResponse } from '@backend/schemas'
import { Button } from '../ui/button'

interface PartMatchingTableProps {
  cabasLines: EstimateResponse['cabasLines']
  invoiceLines: EstimateResponse['invoiceLines']
  matched: EstimateResponse['matchedItems']
}

export const PartMatchingTable = ({ cabasLines, invoiceLines, matched }: PartMatchingTableProps) => {
  const [matches, setMatches] = useState<EstimateResponse['matchedItems']>(matched)

  // Re-sync local matches when the server data changes (e.g. after a "Synka
  // fakturor" sync refetches the case). `matched` comes from the react-query
  // cache, so its identity only changes on an actual refetch — not every render.
  useEffect(() => {
    setMatches(matched)
  }, [matched])

  const handleMatch = (cabasLineIndex: number, invoiceLineIndex: number) => {
    setMatches(current => {
      const newMatches = current.filter(m => m.cabasLineIndex !== cabasLineIndex)
      return [...newMatches, { invoiceLineIndex, cabasLineIndex }]
    })
  }

  const cabasParts = useMemo(() => {
    return cabasLines.map((line, index) => ({
      ...line,
      index,
      matchedInvoiceIndex: matches.find(item => item.cabasLineIndex === index)?.invoiceLineIndex ?? null,
    }))
  }, [cabasLines, matches])

  const invoiceParts = useMemo(() => {
    return invoiceLines.map((line, index) => ({
      ...line,
      index,
      matchedCabasIndex: matches.find(item => item.invoiceLineIndex === index)?.cabasLineIndex ?? null,
    }))
  }, [invoiceLines, matches])

  const unmatchedInvoiceParts = useMemo(() => {
    return invoiceParts.filter(part => {
      return !matches.find(match => match.invoiceLineIndex === part.index)
    })
  }, [invoiceParts, matches])

  const getRowColor = (cabasPrice: number, invoicePrice: number | null) => {
    // Only two decimals for comparison, round up
    const roundedCabasPrice = Math.round(cabasPrice * 100) / 100
    const roundedInvoicePrice = invoicePrice !== null ? Math.round(invoicePrice * 100) / 100 : null

    if (roundedInvoicePrice === null) return ''
    if (roundedInvoicePrice > roundedCabasPrice) return 'bg-red-100 dark:bg-red-950/30'
    if (roundedInvoicePrice < roundedCabasPrice) return 'bg-yellow-100 dark:bg-yellow-950/30'

    return 'bg-green-100 dark:bg-green-950/30'
  }

  const invoiceTotal = invoiceParts
    .filter(part => matches.find(match => match.invoiceLineIndex === part.index))
    .reduce((sum, part) => sum + part.unitPrice * part.quantity, 0)

  const cabasTotal = cabasParts.reduce((sum, part) => {
    const invoicePart = invoiceParts[part.matchedInvoiceIndex]
    return sum + cabasPrice(part, invoicePart?.unitPrice ?? null) * part.Quantity
  }, 0)

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Matchning av delar - Cabas vs Faktura</CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Artikel</TableHead>
                <TableHead>Art.nr</TableHead>
                <TableHead>Antal</TableHead>
                <TableHead>Pris</TableHead>
                <TableHead>Matchad fakturarad</TableHead>
                <TableHead>Faktura Antal</TableHead>
                <TableHead>Faktura Pris</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {cabasParts.map(cabasPart => {
                const invoicePart = invoiceParts[cabasPart.matchedInvoiceIndex]
                const invoicePrice = invoicePart ? invoicePart.unitPrice : null
                const rowColor = getRowColor(cabasPrice(cabasPart, invoicePrice), invoicePrice)

                return (
                  <TableRow key={cabasPart.index} className={rowColor}>
                    <TableCell>{cabasPart.Text}</TableCell>
                    <TableCell className="font-medium">
                      {'CurrentListPrice' in cabasPart ? cabasPart.PartNumber : '-'}
                    </TableCell>
                    <TableCell>{cabasPart.Quantity}</TableCell>
                    <TableCell>
                      {(() => {
                        const { value, type } = cabasPriceDetails(cabasPart, invoicePrice)
                        const formatted = value.toLocaleString('sv-SE', {
                          style: 'currency',
                          currency: 'SEK',
                        })
                        return type ? `${formatted} (${type})` : formatted
                      })()}
                    </TableCell>
                    <TableCell>
                      <Select
                        value={cabasPart.matchedInvoiceIndex !== null ? cabasPart.matchedInvoiceIndex.toString() : ''}
                        onValueChange={value => {
                          if (value === 'clear') {
                            const newMatches = [...matches].filter(m => m.cabasLineIndex !== cabasPart.index)
                            setMatches(newMatches)
                          } else {
                            handleMatch(cabasPart.index, Number(value))
                          }
                        }}
                      >
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Välj fakturarad..." />
                        </SelectTrigger>
                        <SelectContent>
                          {invoicePart && (
                            <SelectItem value="clear" className="text-red-600 font-medium">
                              Rensa matchning
                            </SelectItem>
                          )}
                          {unmatchedInvoiceParts.map(invoicePart => (
                            <SelectItem key={invoicePart.index} value={invoicePart.index.toString()}>
                              {invoicePart.identifier} - {invoicePart.description}
                            </SelectItem>
                          ))}
                          {invoicePart && (
                            <SelectItem value={invoicePart.index.toString()}>
                              {invoicePart.identifier} - {invoicePart.description}
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>{invoicePart?.quantity || '-'}</TableCell>
                    <TableCell>
                      {invoicePart
                        ? invoicePart.unitPrice.toLocaleString('sv-SE', {
                            style: 'currency',
                            currency: 'SEK',
                          })
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {invoicePart && (
                        <Link to={`/invoices/${invoicePart.invoiceId}?carId=${invoicePart.carId}`}>
                          <Button variant="link">
                            <ReceiptText className="h-4 w-4" />
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
              <TableRow>
                <TableCell colSpan={3} className="font-medium">
                  Totalt Cabas
                </TableCell>
                <TableCell className="font-medium">
                  {cabasTotal.toLocaleString('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                  })}
                </TableCell>
                <TableCell colSpan={2} className="font-medium">
                  Totalt fakturor
                </TableCell>
                <TableCell className="font-medium">
                  {invoiceTotal.toLocaleString('sv-SE', {
                    style: 'currency',
                    currency: 'SEK',
                  })}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {unmatchedInvoiceParts.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Omatchade fakturarader</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Delnr</TableHead>
                  <TableHead>Namn</TableHead>
                  <TableHead>Antal</TableHead>
                  <TableHead>Pris</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {unmatchedInvoiceParts.map(part => (
                  <TableRow key={part.index}>
                    <TableCell className="font-medium">{part.identifier}</TableCell>
                    <TableCell>{part.description}</TableCell>
                    <TableCell>{part.quantity}</TableCell>
                    <TableCell>
                      {part.unitPrice.toLocaleString('sv-SE', {
                        style: 'currency',
                        currency: 'SEK',
                      })}
                    </TableCell>
                    {part && (
                      <Link to={`/invoices/${part.invoiceId}?carId=${part.carId}`}>
                        <Button variant="link">
                          <ReceiptText className="h-4 w-4" />
                          <ArrowRight className="h-4 w-4" />
                        </Button>
                      </Link>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
